import * as React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { injectStripe, ReactStripeElements } from 'react-stripe-elements'
import * as validator from 'validator'
import { uuid } from 'uuidv4'

import IContest, { ICompetitorLevel, ITicket } from '../../models/IContest'
import IOrder, { IEntry, IOrderLineItem } from '../../models/IOrder'
import IDictionary from '../../models/IDictionary'
import { ITicketQuantities } from './Tickets'
import OrderFormView from './OrderFormView'
import Confirmation from './Confirmation'
import withServices, { IInjectedServiceProps } from '../../withServices'
// import initState from '../../services/testing'

interface IMatchParams {
  contestSlug: string
}

export interface IOrderFormContainerProps
  extends RouteComponentProps<IMatchParams>,
    IInjectedServiceProps,
    ReactStripeElements.InjectedStripeProps {}

interface IOrderFormContainerState {
  contest?: Readonly<IContest>
  order: Readonly<IOrder>
  ticketQuantities: ITicketQuantities
  isSubmitting: boolean
  selectedLevel?: ICompetitorLevel
  validateOrder: IDictionary<boolean>
  validateEntry: IDictionary<boolean>
  errorMessages?: string[]
}

export class OrderFormContainer extends React.PureComponent<
  IOrderFormContainerProps,
  IOrderFormContainerState
> {
  constructor(props: IOrderFormContainerProps) {
    super(props)

    this.state = {
      isSubmitting: false,
      validateOrder: {},
      validateEntry: {},
      ticketQuantities: {},
      order: {
        contestID: 0,
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        addressLine1: '',
        city: '',
        state: '',
        zip: '',
        lineItems: [],
        idempotencyKey: uuid()
      }
    }
    // this.state = initState()
  }

  public async componentDidMount() {
    const contestSlug = this.props.match.params.contestSlug
    const contest = await this.props.api.getContest(contestSlug)

    const lineItems = this.calculateLineItems(this.state.ticketQuantities, [''])

    // const defaultNANBF = {
    //  entry: {
    //    level: 'NANBF',
    //    last4SSN: '',
    //    divisions: ['']
    //  }
    // }

    this.setState(prevState => ({
      contest,
      // selectedLevel: contest.entryLevels[0],
      order: {
        ...prevState.order,
        // ...defaultNANBF,
        contestID: contest.contestID,
        lineItems
      }
    }))
  }

  public render() {
    const errors = this.validate()

    const {
      contest,
      errorMessages,
      selectedLevel,
      isSubmitting,
      order,
      ticketQuantities,
      validateEntry,
      validateOrder
    } = this.state

    if (order.orderNumber) {
      return <Confirmation order={order} />
    }

    return (
      <OrderFormView
        contest={contest}
        order={order}
        errors={errors}
        errorMessages={errorMessages}
        onCloseError={this.clearErrors}
        selectedLevel={selectedLevel!}
        onSubmit={this.handleSubmit}
        onOrderBlur={this.handleOrderBlur}
        onEntryBlur={this.handleEntryBlur}
        onOrderChange={this.handleOrderChange}
        onEntryChange={this.handleEntryChange}
        validateOrder={validateOrder}
        validateEntry={validateEntry}
        onDateChange={this.handleDateChange}
        ticketQuantities={ticketQuantities}
        onTicketsChange={this.handleTicketsChange}
        onDivisionsChange={this.handleDivisionsChange}
        isSubmitting={isSubmitting}
      />
    )
  }

  public validate = () => {
    const errors: IDictionary<boolean> = {}

    const { selectedLevel } = this.state

    const {
      firstName,
      lastName,
      email,
      phoneNumber,
      addressLine1,
      city,
      state,
      zip
    } = this.state.order

    if (selectedLevel && this.state.order.entry) {
      const {
        birthdate,
        last4SSN,
        heightFeet,
        heightInches,
        divisions
      } = this.state.order.entry

      if (!birthdate) errors.birthdateInvalid = true

      if (last4SSN.trim().length < 4) errors.last4SSNInvalid = true

      if (!heightFeet) errors.heightFeetInvalid = true

      if (!heightInches) errors.heightInchesInvalid = true

      if (divisions.indexOf('') > -1) errors.emptyDivisions = true

      if (
        this.state.selectedLevel &&
        this.state.selectedLevel.requiresWeight &&
        this.state.order.entry.weight === undefined
      ) {
        errors.emptyWeight = true
      }
    }

    if (
      validator.isEmpty(firstName) ||
      validator.isEmpty(lastName) ||
      validator.isEmpty(email) ||
      validator.isEmpty(phoneNumber) ||
      validator.isEmpty(addressLine1) ||
      validator.isEmpty(city) ||
      validator.isEmpty(state) ||
      validator.isEmpty(zip)
    ) {
      errors.requiredFieldsMissing = true
    }

    if (!validator.isMobilePhone(phoneNumber, 'any')) errors.invalidPhone = true

    if (!validator.isEmail(email)) errors.invalidEmail = true

    return errors
  }

  public calculateLineItems = (
    ticketQuantities: ITicketQuantities,
    divisions: string[],
    level?: ICompetitorLevel
  ): IOrderLineItem[] => {
    const lineItems: IOrderLineItem[] = []

    if (level) {
      lineItems.push({
        description: `${level.name} Entry Fee`,
        price: level.price,
        quantity: 1
      })

      if (divisions.length > 1) {
        const quantity = divisions.length - 1
        const description = `Crossover Fee${
          quantity > 1 ? ` x ${quantity}` : ''
        }`
        lineItems.push({
          description,
          quantity: divisions.length - 1,
          price: level.crossoverFee!
        })
      }
    }

    for (const ticketID in ticketQuantities) {
      if (ticketQuantities.hasOwnProperty(ticketID)) {
        const quantity = ticketQuantities[ticketID]

        if (quantity > 0) {
          const ticket = this.getTicket(Number(ticketID))

          const description = `${ticket.name}${
            quantity > 1 ? ` x ${quantity}` : ''
          }`

          lineItems.push({
            description,
            quantity,
            price: ticket.price,
            ticketID: Number(ticketID)
          })
        }
      }
    }

    return lineItems
  }

  private getTicket = (ticketID: number): ITicket => {
    const { contest } = this.state

    let ticket: ITicket | null = null

    contest!.events.map(event => {
      event.tickets.map(t => {
        if (t.ticketID === ticketID) {
          ticket = t
        }
      })
    })

    return ticket!
  }

  private handleDateChange = (date?: Date) => {
    this.setState(prevState => ({
      order: {
        ...prevState.order,
        entry: prevState.order.entry
          ? {
              ...prevState.order.entry,
              birthdate: date
            }
          : undefined
      },
      validateEntry: {
        ...prevState.validateEntry,
        birthdate: true
      }
    }))
  }

  private handleTicketsChange = (ticketID: number, quantity: number) => {
    const {
      ticketQuantities,
      selectedLevel,
      order: { entry }
    } = this.state

    const updatedTicketQuantities = {
      ...ticketQuantities,
      [ticketID]: quantity
    }

    const lineItems = this.calculateLineItems(
      updatedTicketQuantities,
      entry ? entry.divisions : [''],
      selectedLevel
    )

    this.setState(prevState => ({
      ticketQuantities: updatedTicketQuantities,
      order: {
        ...prevState.order,
        lineItems
      }
    }))
  }

  private handleDivisionsChange = (divisions: string[]) => {
    const { ticketQuantities, selectedLevel } = this.state

    const lineItems = this.calculateLineItems(
      ticketQuantities,
      divisions,
      selectedLevel
    )

    this.setState(prevState => ({
      ...prevState,
      order: {
        ...prevState.order,
        entry: prevState.order.entry
          ? {
              ...prevState.order.entry,
              divisions
            }
          : undefined,
        lineItems
      }
    }))
  }

  private handleOrderBlur = (e: any) => {
    const fieldName = e.target.name
    this.setState(prevState => ({
      validateOrder: {
        ...prevState.validateOrder,
        [fieldName]: true
      }
    }))
  }

  private handleEntryBlur = (e: any) => {
    const fieldName = e.target.name

    this.setState(prevState => ({
      validateEntry: {
        ...prevState.validateEntry,
        [fieldName]: true
      }
    }))
  }

  private handleEntryChange = ({ target: { name, value } }) => {
    const { order, contest } = this.state
    let lineItems = order.lineItems
    let selectedLevel = this.state.selectedLevel
    let entry: IEntry | undefined = order.entry

    // this is so messy and ugly:
    if (name === 'level') {
      if (value) {
        if (!order.entry) {
          entry = {
            level: '',
            last4SSN: '',
            divisions: ['']
          }
        }
      } else {
        entry = undefined
      }

      selectedLevel = value && contest!.entryLevels.find(l => l.value === value)

      const { ticketQuantities } = this.state
      lineItems = this.calculateLineItems(
        ticketQuantities,
        selectedLevel ? entry!.divisions : [''],
        selectedLevel
      )
    }

    this.setState(prevState => ({
      ...prevState,
      selectedLevel,
      order: {
        ...prevState.order,
        lineItems,
        entry: entry
          ? {
              ...entry,
              [name]: value,
              divisions: name === 'level' ? [''] : entry.divisions
            }
          : undefined
      }
    }))
  }

  private handleOrderChange = ({ target: { name, value } }) => {
    this.setState(prevState => ({
      ...prevState,
      order: {
        ...prevState.order,
        [name]: value
      }
    }))
  }

  private clearErrors = () => {
    this.setState({ errorMessages: undefined })
  }

  private handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    this.setState({ isSubmitting: true })

    const { api, stripe } = this.props

    const token: ReactStripeElements.PatchedTokenResponse = await stripe!.createToken()

    if (token.error) {
      this.setState({
        errorMessages: [token.error.message!],
        isSubmitting: false
      })
    } else if (token.token) {
      try {
        const orderNumber = await api.submitOrder(
          this.state.order,
          token.token!.id
        )

        this.setState(prevState => ({
          ...prevState,
          order: {
            ...prevState.order,
            orderNumber
          }
        }))
      } catch (e) {
        this.setState({
          errorMessages: [e.message],
          isSubmitting: false
        })
      }
    }
  }
}

export default injectStripe(withServices(OrderFormContainer))



// WEBPACK FOOTER //
// ./src/components/contest/OrderFormContainer.tsx